/**
 * Hard impersonation client — super_admin only.
 *
 * Calls the `impersonate` Edge Function to mint a session for a target user,
 * stashes the original session in sessionStorage, and swaps to the target's
 * session. `stopImpersonation` restores the stashed session.
 *
 * sessionStorage scoping is intentional: an impersonation never outlives
 * the tab it was started in. If the tab closes mid-impersonation, the next
 * sign-in is a fresh login (the stash is gone) — slightly worse UX than
 * automatic restore, but no risk of silently extending an impersonated session.
 */

const STASH_KEY = "ac_portal_impersonator_session";
const TARGET_KEY = "ac_portal_impersonator_target";

export async function startImpersonation(supabase, targetUserId, reason = "") {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  // Stash original session BEFORE the network call so a failed Edge Function
  // call doesn't leave us with no way back.
  sessionStorage.setItem(STASH_KEY, JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  }));

  // Use supabase.functions.invoke — it sends both `apikey` (for project
  // routing) and `Authorization: Bearer <user_jwt>` correctly. Raw fetch()
  // would miss the apikey and get rejected by the Supabase gateway.
  let payload;
  try {
    const { data, error } = await supabase.functions.invoke("impersonate", {
      body: { target_user_id: targetUserId, reason },
    });
    if (error) throw new Error(error.message || "Impersonation failed");
    if (data?.error) throw new Error(data.error);
    payload = data;
  } catch (e) {
    sessionStorage.removeItem(STASH_KEY);
    throw e;
  }

  const { token_hash, target } = payload;
  sessionStorage.setItem(TARGET_KEY, JSON.stringify(target));

  const { error } = await supabase.auth.verifyOtp({
    token_hash,
    type: "magiclink",
  });
  if (error) {
    sessionStorage.removeItem(STASH_KEY);
    sessionStorage.removeItem(TARGET_KEY);
    throw error;
  }

  // Boot will see the new session + the TARGET_KEY stash and render the banner.
  window.location.reload();
}

export async function stopImpersonation(supabase) {
  const stash = sessionStorage.getItem(STASH_KEY);
  if (!stash) {
    // No stash — nothing to restore. Just clear any leftover target marker.
    sessionStorage.removeItem(TARGET_KEY);
    window.location.reload();
    return;
  }

  let session;
  try { session = JSON.parse(stash); } catch { session = null; }

  if (!session?.access_token || !session?.refresh_token) {
    // Corrupted stash — best we can do is sign out and let the user log back in.
    sessionStorage.removeItem(STASH_KEY);
    sessionStorage.removeItem(TARGET_KEY);
    await supabase.auth.signOut();
    window.location.reload();
    return;
  }

  await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  sessionStorage.removeItem(STASH_KEY);
  sessionStorage.removeItem(TARGET_KEY);

  // We're back to the super_admin session — close the audit row so the
  // impersonation_log has a real ended_at timestamp. Order matters: this
  // RPC checks auth.uid() against impersonator_id, so it has to run AFTER
  // setSession swaps us back. Failure here is non-fatal (the row stays
  // open; cosmetic only).
  try { await supabase.rpc("close_impersonation"); }
  catch (e) { console.warn("close_impersonation failed (non-fatal):", e); }

  window.location.reload();
}

export function isImpersonating() {
  return Boolean(sessionStorage.getItem(STASH_KEY));
}

export function impersonationTarget() {
  const raw = sessionStorage.getItem(TARGET_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
